import type {
  HealthResponse,
  QueueStatsResponse,
  DocumentStatsResponse,
  ClassifierStatsResponse,
  ClassifierErrorsResponse,
  RateLimitResponse,
  IconectStatsResponse,
  OneDriveStatsResponse,
  PhiStatsResponse,
  BatchesResponse,
  WorkflowResponse,
  FeederPageRequest,
  FeederPageResponse,
  FeederRecordRequest,
  FeederRecordResponse,
  FeederBatchRecordsRequest,
  FeederBatchRecordsResponse,
  FeederAllRequest,
  FeederAllResponse,
  BulkRetryRequest,
  BulkRetryResponse,
  K8sWorkersResponse,
  K8sVllmResponse,
  K8sScaleResponse,
  DocumentListResponse,
  XeraPresetsResponse,
  DocumentCountResponse,
  SettingsResponse,
  BatchDeletePreviewResponse,
  BatchDeleteResponse,
  GenreOverrideResponse,
  ClassifierConfigResponse,
  FolderMappingResponse,
  RegistryClassifiersResponse,
  RegistryClassifier,
  ClassifierVersionsResponse,
  ClassifierVersionDoc,
  VersionBundlesResponse,
  CreateClassifierPayload,
  CreateVersionPayload,
  BundlesResponse,
  ClassifierBundle,
  ResolvedBundle,
  CreateBundlePayload,
  AdminUser,
  AdminOrg,
  OrgRole,
  AdminMember,
  AdminUsersResponse,
  AdminOrgsResponse,
  AdminMembersResponse,
  AdminMembershipsResponse,
  AdminApiKeysResponse,
  AdminAuditResponse,
  AdminEnvironmentsResponse,
  AdminRestrictedListResponse,
  AdminEnvironmentMetricsResponse,
  AdminEnvironmentRunningResponse,
  AdminEnvironmentModelsResponse,
  AdminEnvironmentInternalDomainsResponse,
  AdminEnvironmentConfigResponse,
  AdminEnvironmentMetricEventsResponse,
  RlcListChange,
  RlcListChangePart,
  RlcScanResult,
  AdminLogEnvironmentsResponse,
  AdminLogWorkloadsResponse,
  AdminLogTailResponse,
  AppEnvironmentResponse,
  RegistryExportPayload,
  RegistryDiff,
  RegistryImportSelections,
  RegistryImportApplyResponse,
  ApiUsageSummary,
  ApiUsageTimeseriesResponse,
  ApiUsageByEndpointResponse,
  ApiUsageByApiKeyResponse,
  ApiUsageByOrgResponse,
  SearchStartParams,
  SearchStartResponse,
  SearchStatusResponse,
  SearchResultsResponse,
  SearchTermStatsResponse,
  SavedSearch,
  SavedSearchesResponse,
  SearchResultIdsResponse,
  VectaraConfigResponse,
  VectaraCorporaResponse,
  VectaraCorpus,
  VectaraContentType,
  VectaraChunking,
  VectaraExportStartResponse,
  VectaraExportStatus,
} from '../types';

// Shared query shape for /api/stats/api-usage/*. Includes an index signature
// so the qs() helper (which takes Record<string, ...>) accepts it without a
// cast.
export interface ApiUsageQuery {
  from: string;
  to: string;
  organizationId?: string;
  userId?: string;
  apiKeyId?: string;
  path?: string;
  [key: string]: string | number | boolean | undefined;
}

const API_BASE = '';

// Options shared by the search results/ids/export endpoints (live + saved).
// requireTerms is the found-term filter: repeated query params, one per term.
export interface SearchResultsOpts {
  limit?: number;
  skip?: number;
  excludeFilter?: string;
  requireTerms?: string[];
}

function searchResultsQuery(opts?: SearchResultsOpts): string {
  const sp = new URLSearchParams();
  if (opts?.limit) sp.set('limit', String(opts.limit));
  if (opts?.skip) sp.set('skip', String(opts.skip));
  if (opts?.excludeFilter && opts.excludeFilter !== 'all') sp.set('excludeFilter', opts.excludeFilter);
  for (const t of opts?.requireTerms ?? []) sp.append('requireTerms', t);
  const q = sp.toString();
  return q ? `?${q}` : '';
}

async function downloadBlobResponse(response: Response, fallbackName: string): Promise<void> {
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const contentDisposition = response.headers.get('Content-Disposition');
  const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
  a.download = filenameMatch?.[1] || fallbackName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options?.headers as Record<string, string>,
  };

  // Auth comes from the hc_token httpOnly cookie set by /api/auth/login.
  // Sending it requires credentials: 'include' on every fetch.
  const response = await fetch(`${API_BASE}${url}`, {
    credentials: 'include',
    ...options,
    headers,
  });

  if (response.status === 401 || response.status === 403) {
    const error = await response.json().catch(() => ({ error: 'Authentication failed' }));
    const msg = error?.error?.message || error?.error || error?.message || 'Authentication failed';
    const err = new Error(msg) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error?.error?.message || error?.error || error?.message || 'Request failed');
  }

  return response.json();
}

export const api = {
  // Health
  getHealth: () => fetchJson<HealthResponse>('/health'),

  // Stats
  getQueueStats: (sampleSize = 100) =>
    fetchJson<QueueStatsResponse>(`/api/stats/queue?sampleSize=${sampleSize}`),

  getDocumentStats: (params?: { addedBy?: string; batchName?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.addedBy) searchParams.set('addedBy', params.addedBy);
    if (params?.batchName) searchParams.set('batchName', params.batchName);
    const query = searchParams.toString();
    return fetchJson<DocumentStatsResponse>(`/api/stats/documents${query ? `?${query}` : ''}`);
  },

  getBatches: () => fetchJson<BatchesResponse>('/api/batches'),

  deleteBatchPreview: (batchName: string) =>
    fetchJson<BatchDeletePreviewResponse>(
      `/api/batches/${encodeURIComponent(batchName)}/delete-preview`
    ),

  deleteBatch: (batchName: string, confirmation: string) =>
    fetchJson<BatchDeleteResponse>(
      `/api/batches/${encodeURIComponent(batchName)}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirmation }),
      }
    ),

  getFileTypes: () => fetchJson<{ fileTypes: string[]; count: number; timestamp: string }>('/api/documents/file-types'),
  getGenres: () => fetchJson<{ genres: string[]; count: number; timestamp: string }>('/api/documents/genres'),

  overrideGenre: (documentId: string, genreLabel: string) =>
    fetchJson<GenreOverrideResponse>(`/api/documents/${encodeURIComponent(documentId)}/genre`, {
      method: 'PATCH',
      body: JSON.stringify({ genreLabel }),
    }),

  getDocuments: (params?: {
    limit?: number;
    skip?: number;
    search?: string;
    status?: string;
    batchName?: string;
    fileType?: string;
    genre?: string;
    sort?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.skip) searchParams.set('skip', params.skip.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.batchName) searchParams.set('batchName', params.batchName);
    if (params?.fileType) searchParams.set('fileType', params.fileType);
    if (params?.genre) searchParams.set('genre', params.genre);
    if (params?.sort) searchParams.set('sort', params.sort);
    const query = searchParams.toString();
    return fetchJson<DocumentListResponse>(`/api/documents${query ? `?${query}` : ''}`);
  },

  exportDocumentsCsv: async (params?: { search?: string; status?: string; batchName?: string; fileType?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.batchName) searchParams.set('batchName', params.batchName);
    if (params?.fileType) searchParams.set('fileType', params.fileType);
    const query = searchParams.toString();

    const headers: Record<string, string> = {};

    const response = await fetch(`${API_BASE}/api/documents/export${query ? `?${query}` : ''}`, { headers, credentials: 'include' });

    if (response.status === 401 || response.status === 403) {
      const error = await response.json().catch(() => ({ error: 'Authentication failed' }));
      throw new Error(error.error || 'Invalid or missing API key');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || 'Export failed');
    }

    // Download the CSV file
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
    a.download = filenameMatch?.[1] || `flagged-sentences-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  startCsvExport: (params?: { search?: string; status?: string; batchName?: string; fileType?: string }) =>
    fetchJson<{ exportId: string; totalDocuments: number; fileName: string }>('/api/documents/export/start', {
      method: 'POST',
      body: JSON.stringify(params || {}),
    }),

  getExportStatus: (exportId: string) =>
    fetchJson<{
      exportId: string;
      status: 'pending' | 'processing' | 'completed' | 'failed';
      totalDocuments: number;
      processedDocuments: number;
      rowCount: number;
      fileName: string;
      error: string | null;
    }>(`/api/documents/export/${exportId}/status`),

  downloadExport: async (exportId: string) => {
    const response = await fetch(`${API_BASE}/api/documents/export/${exportId}/download`, {
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || 'Download failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
    a.download = filenameMatch?.[1] || `flagged-sentences-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  // Advanced Search (term-list search across batches)
  startSearch: (params: SearchStartParams) =>
    fetchJson<SearchStartResponse>('/api/search/start', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  getSearchStatus: (searchId: string) =>
    fetchJson<SearchStatusResponse>(`/api/search/${encodeURIComponent(searchId)}/status`),

  pauseSearch: (searchId: string) =>
    fetchJson<{ ok: boolean }>(`/api/search/${encodeURIComponent(searchId)}/pause`, { method: 'POST' }),
  resumeSearch: (searchId: string) =>
    fetchJson<{ ok: boolean }>(`/api/search/${encodeURIComponent(searchId)}/resume`, { method: 'POST' }),
  cancelSearch: (searchId: string) =>
    fetchJson<{ ok: boolean }>(`/api/search/${encodeURIComponent(searchId)}/cancel`, { method: 'POST' }),

  getSearchResults: (searchId: string, opts?: SearchResultsOpts) =>
    fetchJson<SearchResultsResponse>(
      `/api/search/${encodeURIComponent(searchId)}/results${searchResultsQuery(opts)}`
    ),

  getSearchTermStats: (searchId: string) =>
    fetchJson<SearchTermStatsResponse>(`/api/search/${encodeURIComponent(searchId)}/terms`),

  downloadSearchResults: async (searchId: string, opts?: SearchResultsOpts): Promise<void> => {
    const response = await fetch(
      `${API_BASE}/api/search/${encodeURIComponent(searchId)}/export${searchResultsQuery(opts)}`,
      { credentials: 'include' }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || 'Download failed');
    }
    await downloadBlobResponse(response, `search-results-${new Date().toISOString().slice(0, 10)}.csv`);
  },

  // Saved searches (shared, durable snapshots)
  saveSearch: (searchId: string, body: { name: string; description?: string }) =>
    fetchJson<SavedSearch>(`/api/search/${encodeURIComponent(searchId)}/save`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Make sure a live run has a durable snapshot (auto-named when none exists) —
  // required before refining/deriving so the chain survives the results TTL.
  ensureSearchSaved: (searchId: string) =>
    fetchJson<SavedSearch>(`/api/search/${encodeURIComponent(searchId)}/ensure-saved`, { method: 'POST' }),

  // Materialize the current view filters as a new durable chain step (no re-scan)
  deriveSearch: (searchId: string, body: { requireTerms?: string[]; excludeFilter?: string }) =>
    fetchJson<SavedSearch>(`/api/search/${encodeURIComponent(searchId)}/derive`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deriveSavedSearch: (id: string, body: { requireTerms?: string[]; excludeFilter?: string }) =>
    fetchJson<SavedSearch>(`/api/search/saved/${encodeURIComponent(id)}/derive`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listSavedSearches: () => fetchJson<SavedSearchesResponse>('/api/search/saved'),

  getSavedSearch: (id: string) => fetchJson<SavedSearch>(`/api/search/saved/${encodeURIComponent(id)}`),

  getSavedSearchResults: (id: string, opts?: SearchResultsOpts) =>
    fetchJson<SearchResultsResponse>(
      `/api/search/saved/${encodeURIComponent(id)}/results${searchResultsQuery(opts)}`
    ),

  getSavedSearchTermStats: (id: string) =>
    fetchJson<SearchTermStatsResponse>(`/api/search/saved/${encodeURIComponent(id)}/terms`),

  refreshSavedSearch: (id: string, searchId: string) =>
    fetchJson<SavedSearch>(`/api/search/saved/${encodeURIComponent(id)}/refresh`, {
      method: 'PUT',
      body: JSON.stringify({ searchId }),
    }),

  deleteSavedSearch: (id: string) =>
    fetchJson<{ success: boolean }>(`/api/search/saved/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  downloadSavedSearchResults: async (id: string, opts?: SearchResultsOpts): Promise<void> => {
    const response = await fetch(
      `${API_BASE}/api/search/saved/${encodeURIComponent(id)}/export${searchResultsQuery(opts)}`,
      { credentials: 'include' }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || 'Download failed');
    }
    await downloadBlobResponse(response, `saved-search-${new Date().toISOString().slice(0, 10)}.csv`);
  },

  // Full result-id universe (for select-all-across-pages)
  getSearchResultIds: (searchId: string, opts?: SearchResultsOpts) =>
    fetchJson<SearchResultIdsResponse>(`/api/search/${encodeURIComponent(searchId)}/ids${searchResultsQuery(opts)}`),
  getSavedSearchResultIds: (id: string, opts?: SearchResultsOpts) =>
    fetchJson<SearchResultIdsResponse>(`/api/search/saved/${encodeURIComponent(id)}/ids${searchResultsQuery(opts)}`),

  // Vectara export
  getVectaraConfig: () => fetchJson<VectaraConfigResponse>('/api/vectara/config'),
  listVectaraCorpora: () => fetchJson<VectaraCorporaResponse>('/api/vectara/corpora'),
  createVectaraCorpus: (body: { key: string; name: string; description?: string }) =>
    fetchJson<VectaraCorpus>('/api/vectara/corpora', { method: 'POST', body: JSON.stringify(body) }),
  startVectaraExport: (body: { documentIds: string[]; corpusKey: string; contentType: VectaraContentType; overwrite: boolean; chunking: VectaraChunking }) =>
    fetchJson<VectaraExportStartResponse>('/api/vectara/export/start', { method: 'POST', body: JSON.stringify(body) }),
  getVectaraExportStatus: (exportId: string) =>
    fetchJson<VectaraExportStatus>(`/api/vectara/export/${encodeURIComponent(exportId)}/status`),
  resumeVectaraExport: (exportId: string) =>
    fetchJson<{ ok: boolean }>(`/api/vectara/export/${encodeURIComponent(exportId)}/resume`, { method: 'POST' }),
  cancelVectaraExport: (exportId: string) =>
    fetchJson<{ ok: boolean }>(`/api/vectara/export/${encodeURIComponent(exportId)}/cancel`, { method: 'POST' }),

  getClassifierErrors: (params?: { limit?: number; batchName?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.batchName) searchParams.set('batchName', params.batchName);
    const query = searchParams.toString();
    return fetchJson<ClassifierErrorsResponse>(`/api/stats/classifier-errors${query ? `?${query}` : ''}`);
  },

  getClassifierStats: () => fetchJson<ClassifierStatsResponse>('/api/stats/classifier'),

  resetClassifierStats: () =>
    fetchJson<{ message: string; timestamp: string }>('/api/stats/classifier/reset', {
      method: 'POST',
    }),

  getIconectStats: () => fetchJson<IconectStatsResponse>('/api/stats/iconect'),

  resetIconectStats: () =>
    fetchJson<{ message: string; timestamp: string }>('/api/stats/iconect/reset', {
      method: 'POST',
    }),

  getOneDriveStats: () => fetchJson<OneDriveStatsResponse>('/api/stats/onedrive'),

  resetOneDriveStats: () =>
    fetchJson<{ message: string; timestamp: string }>('/api/stats/onedrive/reset', {
      method: 'POST',
    }),

  getPhiStats: () => fetchJson<PhiStatsResponse>('/api/stats/phi'),

  resetPhiStats: () =>
    fetchJson<{ message: string; timestamp: string }>('/api/stats/phi/reset', {
      method: 'POST',
    }),

  getRateLimits: () => fetchJson<RateLimitResponse>('/api/rate-limits'),

  // Kubernetes
  getK8sWorkers: () => fetchJson<K8sWorkersResponse>('/api/k8s/workers'),
  getK8sVllm: () => fetchJson<K8sVllmResponse>('/api/k8s/vllm'),
  scaleVllm: (replicas: 0 | 1) =>
    fetchJson<K8sScaleResponse>('/api/k8s/vllm/scale', {
      method: 'POST',
      body: JSON.stringify({ replicas }),
    }),
  scaleWorkers: (workerType: string, replicas: number) =>
    fetchJson<K8sScaleResponse>('/api/k8s/workers/scale', {
      method: 'POST',
      body: JSON.stringify({ workerType, replicas }),
    }),

  // Workflows
  getWorkflow: (documentId: string) =>
    fetchJson<WorkflowResponse>(`/api/workflows/${encodeURIComponent(documentId)}`),

  getDocumentFileUrl: (documentId: string, type: 'original' | 'annotated' = 'original') =>
    fetchJson<{ url: string; expiresAt: number; expiresIn: number; type: string }>(
      `/api/documents/${encodeURIComponent(documentId)}/file-url${type !== 'original' ? `?type=${type}` : ''}`
    ),

  getMsgPreview: (documentId: string, signedUrl: string) => {
    // signedUrl already contains expires & sig query params — extract them and call msg-preview
    const params = new URL(signedUrl, window.location.origin).searchParams;
    const expires = params.get('expires');
    const sig = params.get('sig');
    return fetchJson<import('../types').MsgPreviewResponse>(
      `/api/documents/${encodeURIComponent(documentId)}/msg-preview?expires=${expires}&sig=${sig}`
    );
  },

  getDocxPreview: (documentId: string, signedUrl: string) => {
    // signedUrl already contains expires & sig query params — extract them and call docx-preview
    const params = new URL(signedUrl, window.location.origin).searchParams;
    const expires = params.get('expires');
    const sig = params.get('sig');
    return fetchJson<import('../types').DocxPreviewResponse>(
      `/api/documents/${encodeURIComponent(documentId)}/docx-preview?expires=${expires}&sig=${sig}`
    );
  },

  retryWorkflow: (documentId: string) =>
    fetchJson<{ success: boolean; flowId: string }>(`/api/workflows/${encodeURIComponent(documentId)}/retry`, {
      method: 'POST',
    }),

  bulkRetry: (params: BulkRetryRequest) =>
    fetchJson<BulkRetryResponse>('/api/workflows/retry', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // Feeder
  startBatch: (params: FeederPageRequest) =>
    fetchJson<FeederPageResponse>('/api/feeder/page', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  queueSingleRecord: (params: FeederRecordRequest) =>
    fetchJson<FeederRecordResponse>('/api/feeder/records', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  queueBatchRecords: (params: FeederBatchRecordsRequest) =>
    fetchJson<FeederBatchRecordsResponse>('/api/feeder/records/batch', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  processAllDocuments: (params: FeederAllRequest) =>
    fetchJson<FeederAllResponse>('/api/feeder/all', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // Xera Presets
  getPresets: () => fetchJson<XeraPresetsResponse>('/api/xera/presets'),

  getDocumentCount: (presetId: string, pageSize?: number) =>
    fetchJson<DocumentCountResponse>(
      `/api/xera/presets/${encodeURIComponent(presetId)}/document-count${pageSize ? `?pageSize=${pageSize}` : ''}`
    ),

  // Settings
  getSettings: () => fetchJson<SettingsResponse>('/api/settings'),

  // Classifier config (legacy read-only)
  getClassifierConfig: () => fetchJson<ClassifierConfigResponse>('/api/classifier-config/classifiers'),
  getFolderMapping: () => fetchJson<FolderMappingResponse>('/api/classifier-config/folder-mapping'),

  // Classifier registry (CRUD)
  getRegistryClassifiers: (active?: boolean) => {
    const query = active !== undefined ? `?active=${active}` : '';
    return fetchJson<RegistryClassifiersResponse>(`/api/registry/classifiers${query}`);
  },
  getRegistryClassifier: (key: string) =>
    fetchJson<RegistryClassifier>(`/api/registry/classifiers/${key}`),
  createClassifier: (data: CreateClassifierPayload) =>
    fetchJson<RegistryClassifier>('/api/registry/classifiers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateClassifier: (key: string, data: Partial<RegistryClassifier>) =>
    fetchJson<RegistryClassifier>(`/api/registry/classifiers/${key}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteClassifier: (key: string) =>
    fetchJson<RegistryClassifier>(`/api/registry/classifiers/${key}`, {
      method: 'DELETE',
    }),
  getClassifierVersions: (key: string) =>
    fetchJson<ClassifierVersionsResponse>(`/api/registry/classifiers/${key}/versions`),
  getClassifierVersion: (key: string, version: string) =>
    fetchJson<ClassifierVersionDoc>(`/api/registry/classifiers/${key}/versions/${encodeURIComponent(version)}`),
  getVersionBundles: (key: string, version: string) =>
    fetchJson<VersionBundlesResponse>(`/api/registry/classifiers/${key}/versions/${encodeURIComponent(version)}/bundles`),
  createVersion: (key: string, data: CreateVersionPayload) =>
    fetchJson<ClassifierVersionDoc>(`/api/registry/classifiers/${key}/versions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateVersion: (key: string, version: string, data: { status?: string; notes?: string; mutedLabels?: string[]; labelOverrides?: Record<string, string> }) =>
    fetchJson<ClassifierVersionDoc>(`/api/registry/classifiers/${key}/versions/${encodeURIComponent(version)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  setCurrentVersion: (key: string, version: string) =>
    fetchJson<RegistryClassifier>(`/api/registry/classifiers/${key}/set-current`, {
      method: 'POST',
      body: JSON.stringify({ version }),
    }),

  // Classifier bundles
  getBundles: (active?: boolean) => {
    const query = active !== undefined ? `?active=${active}` : '';
    return fetchJson<BundlesResponse>(`/api/registry/bundles${query}`);
  },
  getBundle: (key: string) =>
    fetchJson<ClassifierBundle>(`/api/registry/bundles/${key}`),
  resolveBundle: (key: string) =>
    fetchJson<ResolvedBundle>(`/api/registry/bundles/${key}/resolve`),
  createBundle: (data: CreateBundlePayload) =>
    fetchJson<ClassifierBundle>('/api/registry/bundles', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateBundle: (key: string, data: Partial<ClassifierBundle>) =>
    fetchJson<ClassifierBundle>(`/api/registry/bundles/${key}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteBundle: (key: string) =>
    fetchJson<ClassifierBundle>(`/api/registry/bundles/${key}`, {
      method: 'DELETE',
    }),
  setBundleDefault: (key: string) =>
    fetchJson<ClassifierBundle>(`/api/registry/bundles/${key}/set-default`, {
      method: 'POST',
    }),
  duplicateBundle: (
    sourceKey: string,
    data: { key: string; name: string; description?: string }
  ) =>
    fetchJson<ClassifierBundle>(`/api/registry/bundles/${sourceKey}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // App environment (lightweight — just which deploy this dashboard hits)
  getAppEnvironment: () => fetchJson<AppEnvironmentResponse>('/api/settings/app-environment'),

  // Classifier registry export/import — both fetchers trigger a browser
  // download. The server sets Content-Disposition with a sensible filename
  // (e.g. bundle-iconect-default-staging-2026-05-12.json).
  exportBundle: async (bundleKey: string): Promise<void> => {
    const response = await fetch(
      `${API_BASE}/api/registry/bundles/${encodeURIComponent(bundleKey)}/export`,
      { credentials: 'include' }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || 'Export failed');
    }
    await downloadBlobResponse(response, `bundle-${bundleKey}.json`);
  },
  exportRegistry: async (): Promise<void> => {
    const response = await fetch(`${API_BASE}/api/registry/export`, { credentials: 'include' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || 'Export failed');
    }
    await downloadBlobResponse(response, 'registry.json');
  },
  previewRegistryImport: (payload: RegistryExportPayload) =>
    fetchJson<RegistryDiff>('/api/registry/import/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  applyRegistryImport: (payload: RegistryExportPayload, selections: RegistryImportSelections) =>
    fetchJson<RegistryImportApplyResponse>('/api/registry/import/apply', {
      method: 'POST',
      body: JSON.stringify({ payload, selections }),
    }),

  // Admin
  flushQueuesPreview: () =>
    fetchJson<{
      totalKeys: number;
      prefix: string;
      summary: Record<string, number>;
      sampleKeys: string[];
    }>('/api/admin/flush-queues/preview'),

  flushQueues: (confirmation: string) =>
    fetchJson<{
      success: boolean;
      message: string;
      keysDeleted: number;
      note?: string;
    }>('/api/admin/flush-queues', {
      method: 'POST',
      body: JSON.stringify({ confirmation }),
    }),

  // Full system reset
  resetAllPreview: () =>
    fetchJson<{
      preview: {
        queues: { keysToDelete: number; prefix: string };
        idempotency: { processedDocumentsToCllear: number };
        rateLimiterMetrics: { note: string };
        mongodb: { note: string };
      };
    }>('/api/admin/reset-all/preview'),

  resetAll: (confirmation: string) =>
    fetchJson<{
      success: boolean;
      message: string;
      results: {
        queues: { success: boolean; keysDeleted: number };
        idempotency: { success: boolean; documentsCleared: number };
        rateLimiterMetrics: { success: boolean };
      };
      notes: string[];
    }>('/api/admin/reset-all', {
      method: 'POST',
      body: JSON.stringify({ confirmation }),
    }),

  // Queue control
  pauseQueue: (queueName: string) =>
    fetchJson<{
      success: boolean;
      queue: string;
      isPaused: boolean;
      message: string;
      timestamp: string;
    }>(`/api/queues/${encodeURIComponent(queueName)}/pause`, {
      method: 'POST',
    }),

  resumeQueue: (queueName: string) =>
    fetchJson<{
      success: boolean;
      queue: string;
      isPaused: boolean;
      message: string;
      timestamp: string;
    }>(`/api/queues/${encodeURIComponent(queueName)}/resume`, {
      method: 'POST',
    }),

  retryQueueFailed: (queueName: string, params?: { dryRun?: boolean; limit?: number }) =>
    fetchJson<BulkRetryResponse>(`/api/queues/${encodeURIComponent(queueName)}/retry-failed`, {
      method: 'POST',
      body: JSON.stringify(params || {}),
    }),

  // External batch submission
  submitExternalBatch: (params: { documents: { url: string; documentId?: string }[]; batchName?: string; folderName?: string; skipProcessed?: boolean }) =>
    fetchJson<{ success: boolean; batchId: string; batchName: string; summary: { total: number; queued: number; skipped: number; failed: number }; message: string }>(
      '/api/external/submit-batch',
      { method: 'POST', body: JSON.stringify(params) }
    ),

  // Upload PDF files for processing
  uploadFiles: async (params: { files: File[]; batchName?: string; skipProcessed?: boolean; bundleKey?: string }) => {
    const formData = new FormData();
    for (const file of params.files) {
      formData.append('files', file);
    }
    if (params.batchName) {
      formData.append('batchName', params.batchName);
    }
    formData.append('skipProcessed', String(params.skipProcessed ?? true));
    if (params.bundleKey) {
      formData.append('bundleKey', params.bundleKey);
    }

    // Do NOT set Content-Type — browser sets it with multipart boundary
    const response = await fetch('/api/upload/files', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (response.status === 401 || response.status === 403) {
      const error = await response.json().catch(() => ({ error: 'Authentication failed' }));
      throw new Error(error.error || 'Invalid or missing API key');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || 'Upload failed');
    }

    return response.json() as Promise<import('../types').UploadFilesResponse>;
  },

  // Scrape justice.gov dataset pages and submit discovered PDFs
  scrapeJustice: (params: { dataset: number; startPage?: number; endPage?: number; batchName?: string; skipProcessed?: boolean }) =>
    fetchJson<{ success: boolean; batchId: string; batchName: string; summary: { total: number; queued: number; skipped: number; failed: number; pagesScraped: number }; message: string; warning?: string }>(
      '/api/external/scrape-justice',
      { method: 'POST', body: JSON.stringify(params) }
    ),
};

// =============================================================================
// Admin API client
// =============================================================================

function qs(params: Record<string, string | number | boolean | undefined>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const adminApi = {
  // Users
  listUsers: (params: { search?: string; limit?: number; skip?: number } = {}) =>
    fetchJson<AdminUsersResponse>(`/api/admin/users${qs(params)}`),
  getUser: (id: string) => fetchJson<{ user: AdminUser }>(`/api/admin/users/${id}`),
  verifyUser: (id: string) =>
    fetchJson<{ user: AdminUser; message: string }>(`/api/admin/users/${id}/verify`, { method: 'PUT' }),
  setUserAdmin: (id: string, isAlphyAdmin: boolean) =>
    fetchJson<{ user: AdminUser; message: string }>(`/api/admin/users/${id}/admin`, {
      method: 'PUT',
      body: JSON.stringify({ isAlphyAdmin }),
    }),
  getUserMemberships: (id: string) =>
    fetchJson<AdminMembershipsResponse>(`/api/admin/users/${id}/memberships`),
  getUserApiKeys: (id: string) =>
    fetchJson<AdminApiKeysResponse>(`/api/admin/users/${id}/api-keys`),

  // Organizations
  listOrgs: (params: { search?: string; limit?: number; skip?: number } = {}) =>
    fetchJson<AdminOrgsResponse>(`/api/admin/orgs${qs(params)}`),
  getOrg: (id: string) => fetchJson<{ organization: AdminOrg }>(`/api/admin/orgs/${id}`),
  createOrg: (body: { name: string; slug: string; ownerEmail?: string }) =>
    fetchJson<{ organization: AdminOrg; ownerMembership: { id: string; userId: string } | null }>(
      '/api/admin/orgs',
      { method: 'POST', body: JSON.stringify(body) }
    ),
  updateOrg: (id: string, body: Partial<AdminOrg>) =>
    fetchJson<{ organization: AdminOrg }>(`/api/admin/orgs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteOrg: (id: string) =>
    fetchJson<{ message: string; removedMemberships: number }>(`/api/admin/orgs/${id}`, {
      method: 'DELETE',
    }),
  getOrgMembers: (id: string) => fetchJson<AdminMembersResponse>(`/api/admin/orgs/${id}/members`),
  addOrgMember: (id: string, body: { email: string; role: OrgRole }) =>
    fetchJson<{ member: AdminMember }>(`/api/admin/orgs/${id}/members`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  changeOrgMemberRole: (id: string, userId: string, role: OrgRole) =>
    fetchJson<{ member: AdminMember }>(`/api/admin/orgs/${id}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  removeOrgMember: (id: string, userId: string) =>
    fetchJson<{ message: string }>(`/api/admin/orgs/${id}/members/${userId}`, {
      method: 'DELETE',
    }),
  getOrgApiKeys: (id: string) =>
    fetchJson<AdminApiKeysResponse>(`/api/admin/orgs/${id}/api-keys`),

  // API Keys
  listApiKeys: (params: {
    orgId?: string;
    userId?: string;
    active?: boolean;
    environment?: 'live' | 'test';
    search?: string;
    limit?: number;
    skip?: number;
  } = {}) =>
    fetchJson<AdminApiKeysResponse>(`/api/admin/api-keys${qs(params)}`),
  revokeApiKey: (id: string) =>
    fetchJson<{ message: string; apiKey: { id: string; prefix: string } }>(
      `/api/admin/api-keys/${id}/revoke`,
      { method: 'POST' }
    ),

  // Audit
  listAudit: (params: {
    actor?: string;
    actorId?: string;
    targetType?: string;
    targetId?: string;
    action?: string;
    actionPrefix?: string;
    since?: string;
    until?: string;
    limit?: number;
    skip?: number;
  } = {}) => fetchJson<AdminAuditResponse>(`/api/admin/audit${qs(params)}`),

  // API usage (request-volume aggregations over the ApiRequest log).
  getApiUsageSummary: (params: ApiUsageQuery) =>
    fetchJson<ApiUsageSummary>(`/api/stats/api-usage/summary${qs(params)}`),
  getApiUsageTimeseries: (params: ApiUsageQuery & { granularity?: 'auto' | 'hour' | 'day' }) =>
    fetchJson<ApiUsageTimeseriesResponse>(`/api/stats/api-usage/timeseries${qs(params)}`),
  getApiUsageByEndpoint: (params: ApiUsageQuery) =>
    fetchJson<ApiUsageByEndpointResponse>(`/api/stats/api-usage/by-endpoint${qs(params)}`),
  getApiUsageByApiKey: (params: ApiUsageQuery) =>
    fetchJson<ApiUsageByApiKeyResponse>(`/api/stats/api-usage/by-api-key${qs(params)}`),
  getApiUsageByOrg: (params: ApiUsageQuery) =>
    fetchJson<ApiUsageByOrgResponse>(`/api/stats/api-usage/by-org${qs(params)}`),

  // Environments (RLC environment catalog)
  listEnvironments: (params: { refresh?: boolean } = {}) =>
    fetchJson<AdminEnvironmentsResponse>(`/api/admin/environments${qs({ refresh: params.refresh ? 1 : undefined })}`),
  getEnvironmentRestrictedList: (key: string, params: { q?: string; limit?: number; offset?: number; refresh?: boolean } = {}) =>
    fetchJson<AdminRestrictedListResponse>(
      `/api/admin/environments/${encodeURIComponent(key)}/restricted-list${qs({
        q: params.q,
        limit: params.limit,
        offset: params.offset || undefined,
        refresh: params.refresh ? 1 : undefined,
      })}`
    ),
  changeEnvironmentRestrictedList: (key: string, body: { fingerprint: string; add: string[]; remove: string[] }) =>
    fetchJson<{ change: RlcListChange }>(`/api/admin/environments/${encodeURIComponent(key)}/restricted-list/changes`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  replaceEnvironmentRestrictedList: (key: string, body: { fingerprint: string; terms: string[]; source?: string; dryRun?: boolean }) =>
    fetchJson<{ change: RlcListChange }>(`/api/admin/environments/${encodeURIComponent(key)}/restricted-list/replace`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listEnvironmentRestrictedListChanges: (key: string) =>
    fetchJson<{ changes: RlcListChange[] }>(`/api/admin/environments/${encodeURIComponent(key)}/restricted-list/changes`),
  environmentListChangeUrl: (key: string, id: string, part: RlcListChangePart) =>
    `${API_BASE}/api/admin/environments/${encodeURIComponent(key)}/restricted-list/changes/${encodeURIComponent(id)}/${part}`,
  runEnvironmentScan: (key: string, body: { email: string; subject: string; body: string }) =>
    fetchJson<RlcScanResult>(`/api/admin/environments/${encodeURIComponent(key)}/scan`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reportEnvironmentScan: (
    key: string,
    body: { email: string; subject: string; body: string; correlationId: string; responseTimeMs: number; outcome: NonNullable<RlcScanResult['outcome']> }
  ) =>
    fetchJson<{ reported: boolean; eventType: string }>(`/api/admin/environments/${encodeURIComponent(key)}/scan/report`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getEnvironmentMetrics: (key: string, days: number) =>
    fetchJson<AdminEnvironmentMetricsResponse>(`/api/admin/environments/${encodeURIComponent(key)}/metrics${qs({ days })}`),
  getEnvironmentMetricEvents: (key: string, params: { limit?: number; skip?: number; eventType?: string; userEmail?: string }) =>
    fetchJson<AdminEnvironmentMetricEventsResponse>(
      `/api/admin/environments/${encodeURIComponent(key)}/metrics/events${qs(params)}`
    ),
  getEnvironmentRunning: (key: string, refresh = false) =>
    fetchJson<AdminEnvironmentRunningResponse>(
      `/api/admin/environments/${encodeURIComponent(key)}/running${qs({ refresh: refresh ? 1 : undefined })}`
    ),
  getEnvironmentModels: (key: string, refresh = false) =>
    fetchJson<AdminEnvironmentModelsResponse>(
      `/api/admin/environments/${encodeURIComponent(key)}/models${qs({ refresh: refresh ? 1 : undefined })}`
    ),
  getEnvironmentInternalDomains: (key: string, refresh = false) =>
    fetchJson<AdminEnvironmentInternalDomainsResponse>(
      `/api/admin/environments/${encodeURIComponent(key)}/internal-domains${qs({ refresh: refresh ? 1 : undefined })}`
    ),
  changeEnvironmentInternalDomains: (key: string, body: { fingerprint: string; add: string[]; remove: string[] }) =>
    fetchJson<{ change: RlcListChange }>(`/api/admin/environments/${encodeURIComponent(key)}/internal-domains/changes`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listEnvironmentInternalDomainChanges: (key: string) =>
    fetchJson<{ changes: RlcListChange[] }>(`/api/admin/environments/${encodeURIComponent(key)}/internal-domains/changes`),
  getEnvironmentConfig: (key: string, refresh = false) =>
    fetchJson<AdminEnvironmentConfigResponse>(
      `/api/admin/environments/${encodeURIComponent(key)}/config${qs({ refresh: refresh ? 1 : undefined })}`
    ),
  environmentManifestUrl: (key: string) =>
    `${API_BASE}/api/admin/environments/${encodeURIComponent(key)}/manifest`,

  // Logs (CloudWatch Insights tail)
  listLogEnvironments: () =>
    fetchJson<AdminLogEnvironmentsResponse>('/api/admin/logs/environments'),
  listLogWorkloads: () =>
    fetchJson<AdminLogWorkloadsResponse>('/api/admin/logs/workloads'),
  tailLogs: (params: { env: string; workload: string; pattern?: string; minutes?: number; limit?: number }) =>
    fetchJson<AdminLogTailResponse>(`/api/admin/logs/tail${qs(params)}`),
};

// SWR fetcher
export const fetcher = <T>(url: string): Promise<T> => fetchJson<T>(url);
